import { useState } from "react";
import { motion } from "framer-motion";
import { FaPaperPlane, FaGithub, FaLinkedin, FaTelegram, FaWhatsapp, FaInstagram, FaEnvelope } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import Headline from "../shared/Headline";

const Contact = () => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", message: "" });
  const [error, setError] = useState({});
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error[e.target.name]) {
      setError({ ...error, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const errors = {};
    const nameRegex = /^[a-zA-Z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.firstName.trim()) errors.firstName = "Required";
    else if (!nameRegex.test(form.firstName)) errors.firstName = "Letters only";

    if (!form.lastName.trim()) errors.lastName = "Required";
    else if (!nameRegex.test(form.lastName)) errors.lastName = "Letters only";

    if (!form.email.trim()) errors.email = "Required";
    else if (!emailRegex.test(form.email)) errors.email = "Invalid email";

    if (!form.phone.trim()) errors.phone = "Required";

    if (!form.message.trim()) errors.message = "Required";
    else if (form.message.split(" ").length > 500) errors.message = "Max 500 words";

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess("");
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setSending(true);
    const templateParams = {
      from_email: form.email,
      to_name: "Dagmawi Ephrem",
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      message: form.message,
    };

    emailjs
      .send("service_oqxbeqa", "template_4jtgh5s", templateParams, "YScyqLRJxdFCfROZQ")
      .then(() => {
        setSuccess("Message sent successfully! I'll get back to you soon.");
        setForm({ firstName: "", lastName: "", email: "", phone: "", message: "" });
      })
      .catch(() => {
        setError({ global: "Failed to send. Please try again." });
      })
      .finally(() => setSending(false));
  };

  const socials = [
    { icon: <FaGithub className="w-5 h-5" />, href: "https://github.com/MickeyDagm", label: "GitHub" },
    { icon: <FaLinkedin className="w-5 h-5" />, href: "https://www.linkedin.com/in/dagmawi-ephrem-559a7b342", label: "LinkedIn" },
    { icon: <FaTelegram className="w-5 h-5" />, href: "https://t.me/dagi_0056", label: "Telegram" },
    { icon: <FaWhatsapp className="w-5 h-5" />, href: "https://wa.me/qr/VPX75GG4PGCCP1", label: "WhatsApp" },
    { icon: <FaInstagram className="w-5 h-5" />, href: "https://www.instagram.com/mickey_dagm/profilecard/?igsh=MW4xeTRwM3YxdWtzMw==", label: "Instagram" },
  ];

  const inputClass = "w-full px-4 py-3 rounded-xl dark:bg-white/5 bg-gray-50 border dark:border-white/10 border-gray-300 dark:text-white text-gray-900 dark:placeholder-gray-500 placeholder-gray-400 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-all duration-300";

  return (
    <section id="contact" className="px-6 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(99,102,241,0.05),transparent_60%)]" />
      <div className="container-custom relative">
        <Headline
          title="Get In Touch"
          subtitle="Have a project in mind? Let's build something great together."
        />

        <div className="max-w-4xl mx-auto grid md:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-2"
          >
            <div className="glass rounded-2xl p-6 space-y-5">
              <h3 className="text-lg font-semibold text-white">Let's talk</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                I'm always open to new opportunities, collaborations, and interesting projects.
                Feel free to reach out!
              </p>

              <div className="flex items-center gap-3 text-sm text-gray-400">
                <FaEnvelope className="w-5 h-5 text-primary-400" />
                <span>dagmmike1224@gmail.com</span>
              </div>

              <div className="flex gap-3 pt-2">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 glass rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                    aria-label={s.label}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="md:col-span-3 glass rounded-2xl p-6 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <input type="text" name="firstName" placeholder="First Name" value={form.firstName} onChange={handleChange} className={inputClass} />
                {error.firstName && <p className="text-red-400 text-xs mt-1">{error.firstName}</p>}
              </div>
              <div>
                <input type="text" name="lastName" placeholder="Last Name" value={form.lastName} onChange={handleChange} className={inputClass} />
                {error.lastName && <p className="text-red-400 text-xs mt-1">{error.lastName}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <input type="email" name="email" placeholder="Email" value={form.email} onChange={handleChange} className={inputClass} />
                {error.email && <p className="text-red-400 text-xs mt-1">{error.email}</p>}
              </div>
              <div>
                <input type="tel" name="phone" placeholder="Phone Number" value={form.phone} onChange={handleChange} className={inputClass} />
                {error.phone && <p className="text-red-400 text-xs mt-1">{error.phone}</p>}
              </div>
            </div>

            <div>
              <textarea name="message" rows="5" placeholder="Your message (max 500 words)" value={form.message} onChange={handleChange} className={inputClass + " resize-none"} />
              {error.message && <p className="text-red-400 text-xs mt-1">{error.message}</p>}
            </div>

            <button
              type="submit"
              disabled={sending}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                "Sending..."
              ) : (
                <>
                  <FaPaperPlane className="w-4 h-4" /> Send Message
                </>
              )}
            </button>

            {error.global && <p className="text-red-400 text-sm text-center">{error.global}</p>}
            {success && <p className="text-green-400 text-sm text-center">{success}</p>}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
